import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from "@shadcn/components/ui/form";
import { getFieldHelperText } from "@forms/form-render/helpers/getFieldHelperText";
import { Input } from "@shadcn/components/ui/input";
import { Button } from "@shadcn/components/ui/button";
import { Loader2, MapPin, Hospital } from "lucide-react";
import { placeApi, PlaceAutocompleteResult, PlaceDetailsResult } from "./api";
import { GooglePlaceSearchProps } from "./GooglePlaceSearch.types";

export const GooglePlaceSearch = ({
  name,
  required,
  disabled,
  helperText,
  placeTypes,
  showManualEntryButton = true,
}: GooglePlaceSearchProps) => {
  const description = getFieldHelperText(helperText, required);
  const { control, setValue, watch } = useFormContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState<PlaceAutocompleteResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<PlaceDetailsResult | null>(null);
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationRequested, setLocationRequested] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const sessionTokenRef = useRef<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const found = watch(`${name}.found`) as boolean | undefined;
  const selectedName = watch(`${name}.name`) as string | undefined;
  const selectedFormattedAddress = watch(`${name}.formattedAddress`) as string | undefined;

  const clearAnswer = useCallback(() => {
    setValue(`${name}.found`, undefined);
    setValue(`${name}.placeId`, undefined);
    setValue(`${name}.name`, undefined);
    setValue(`${name}.formattedAddress`, undefined);
    setValue(`${name}.primaryType`, undefined);
    setValue(`${name}.town`, undefined);
    setValue(`${name}.postalCode`, undefined);
    setValue(`${name}.countryIso2`, undefined);
    setSelectedPlace(null);
    setManualMode(false);
  }, [name, setValue]);

  const requestUserLocation = useCallback(() => {
    if (locationRequested || userLocation || !navigator.geolocation) {
      return;
    }

    setLocationRequested(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      () => {
        setLocationRequested(false);
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, [locationRequested, userLocation]);

  const performSearch = useCallback(
    async (query: string) => {
      if (!query || query.length < 3 || !placeTypes?.length) {
        setResults([]);
        setShowDropdown(false);
        return;
      }

      if (!sessionTokenRef.current) {
        sessionTokenRef.current = generateUUID();
      }

      setIsSearching(true);
      setShowDropdown(true);

      try {
        const response = await placeApi.search({
          query,
          placeTypes,
          location: userLocation || undefined,
          sessionToken: sessionTokenRef.current ?? undefined,
        });
        setResults(response.results || []);
        setHighlightedIndex(null);
      } catch (error) {
        console.error("Place search error:", error);
        setResults([]);
        setHighlightedIndex(null);
      } finally {
        setIsSearching(false);
      }
    },
    [placeTypes, userLocation]
  );

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (searchQuery) {
        void performSearch(searchQuery);
      } else {
        setResults([]);
        setShowDropdown(false);
      }
    }, 600);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, performSearch]);

  useEffect(() => {
    if (found === true && selectedName && !selectedPlace) {
      setSelectedPlace({
        placeId: watch(`${name}.placeId`) || "",
        name: selectedName,
        formattedAddress: selectedFormattedAddress || "",
        primaryType: watch(`${name}.primaryType`) || "",
        postalCode: watch(`${name}.postalCode`) || "",
        postalTown: watch(`${name}.town`) || "",
        country: "",
        countryIso2: watch(`${name}.countryIso2`) || "",
      });
    }
    if (found === false && selectedName) {
      setManualMode(true);
    }
  }, [found, selectedName, selectedFormattedAddress, selectedPlace, name, watch]);

  const applyPlace = useCallback(
    (place: PlaceDetailsResult) => {
      setValue(`${name}.found`, true);
      setValue(`${name}.placeId`, place.placeId);
      setValue(`${name}.name`, place.name);
      setValue(`${name}.formattedAddress`, place.formattedAddress || "");
      setValue(`${name}.primaryType`, place.primaryType || "");
      setValue(`${name}.town`, place.postalTown || "");
      setValue(`${name}.postalCode`, place.postalCode || "");
      setValue(`${name}.countryIso2`, place.countryIso2 || "");
      setSelectedPlace(place);
      setManualMode(false);
      setSearchQuery("");
      setResults([]);
      setShowDropdown(false);
    },
    [name, setValue]
  );

  const handleSelectPlace = useCallback(
    async (autocompleteResult: PlaceAutocompleteResult) => {
      setIsFetchingDetails(true);
      setShowDropdown(false);
      setSearchQuery(autocompleteResult.text);

      try {
        const token = sessionTokenRef.current ?? undefined;
        const detailsResponse = await placeApi.getDetails({
          placeId: autocompleteResult.placeId,
          sessionToken: token,
        });
        sessionTokenRef.current = null;
        applyPlace(detailsResponse.result);
      } catch (error) {
        console.error("Failed to fetch place details:", error);
        setShowDropdown(true);
      } finally {
        setIsFetchingDetails(false);
      }
    },
    [applyPlace]
  );

  const handleManualMode = () => {
    clearAnswer();
    setManualMode(true);
    setValue(`${name}.found`, false);
    setValue(`${name}.name`, "");
    setShowDropdown(false);
    setSearchQuery("");
  };

  const handleSearchAgain = () => {
    sessionTokenRef.current = null;
    clearAnswer();
    setSearchQuery("");
    setResults([]);
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showDropdown || results.length === 0 || isSearching || isFetchingDetails) {
        if (e.key === "Escape") {
          setShowDropdown(false);
          setHighlightedIndex(null);
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev === null || prev >= results.length - 1 ? 0 : prev + 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev === null || prev <= 0 ? results.length - 1 : prev - 1));
          break;
        case "Enter":
          e.preventDefault();
          if (highlightedIndex !== null) {
            void handleSelectPlace(results[highlightedIndex]);
          }
          break;
        case "Escape":
          setShowDropdown(false);
          setHighlightedIndex(null);
          break;
      }
    },
    [showDropdown, results, isSearching, isFetchingDetails, highlightedIndex, handleSelectPlace]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setHighlightedIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const showSelected = found === true && Boolean(selectedName) && !manualMode;
  const showSearch = !showSelected && !manualMode && !isFetchingDetails;

  return (
    <div className="space-y-4">
      {showSearch && (
        <div ref={containerRef} className="relative">
          <FormField
            control={control}
            name={`${name}.placeSearch`}
            rules={
              required
                ? {
                    validate: () => {
                      if (found === true || (found === false && selectedName)) return true;
                      return "Please search for a place";
                    },
                  }
                : undefined
            }
            render={() => (
              <FormItem>
                {description && <FormDescription>{description}</FormDescription>}
                <FormControl>
                  <div className="relative">
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={requestUserLocation}
                      onKeyDown={handleKeyDown}
                      placeholder="Search for a place..."
                      disabled={disabled || isFetchingDetails}
                      autoComplete="off"
                    />
                    {(isSearching || isFetchingDetails) && (
                      <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {showDropdown && results.length > 0 && (
            <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md">
              {results.map((result, index) => (
                <button
                  key={result.placeId}
                  type="button"
                  className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent ${
                    highlightedIndex === index ? "bg-accent" : ""
                  }`}
                  onClick={() => void handleSelectPlace(result)}
                  disabled={disabled}
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span>{result.text}</span>
                </button>
              ))}
            </div>
          )}

          {showManualEntryButton && (
            <div className="mt-2">
              <Button type="button" variant="link" className="h-auto p-0" onClick={handleManualMode} disabled={disabled}>
                Enter place name manually
              </Button>
            </div>
          )}
        </div>
      )}

      {isFetchingDetails && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading place details...
        </div>
      )}

      {showSelected && (
        <div className="space-y-3 rounded-md border p-4">
          <div className="flex items-start gap-3">
            <Hospital className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="font-medium">{selectedName}</p>
              {selectedFormattedAddress && (
                <p className="text-sm text-muted-foreground">{selectedFormattedAddress}</p>
              )}
            </div>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={handleSearchAgain} disabled={disabled}>
            Search again
          </Button>
        </div>
      )}

      {manualMode && (
        <div className="space-y-3">
          {description && <FormDescription>{description}</FormDescription>}
          <FormField
            control={control}
            name={`${name}.name`}
            rules={
              required
                ? {
                    validate: (value) => (value && String(value).trim() ? true : "Place name is required"),
                  }
                : undefined
            }
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    placeholder="Place name"
                    disabled={disabled}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setValue(`${name}.found`, false);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="button" variant="outline" size="sm" onClick={handleSearchAgain} disabled={disabled}>
            Search again
          </Button>
        </div>
      )}
    </div>
  );
};

function generateUUID(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
